import { useState, useMemo, useCallback } from 'react';
import type { Language, Difficulty } from '@codedrill/shared';
import type { SidebarItemId, SidebarItem } from '../types/typing';
import type { ExerciseDifficulty } from '../data/englishExercises';
import { useSnippet } from '../hooks/useSnippet';
import { useEnglishExercise } from '../hooks/useEnglishExercise';
import { useTypingSession } from '../hooks/useTypingSession';
import { useKeystrokeHeatmap } from '../hooks/useKeystrokeHeatmap';
import { useSessionResult } from '../hooks/useSessionResult';
import { useTypingStore } from '../store/useTypingStore';
import { preprocessText } from '../engine/TextPreprocessor';

import LanguageSidebar from '../components/ui/LanguageSidebar';
import WelcomeView from '../components/ui/WelcomeView';
import DifficultySelector from '../components/ui/DifficultySelector';
import SnippetLoader from '../components/snippet/SnippetLoader';
import SnippetDisplay from '../components/snippet/SnippetDisplay';
import TypingArea from '../components/typing/TypingArea';
import ProgressBar from '../components/typing/ProgressBar';
import LiveMetrics from '../components/metrics/LiveMetrics';
import ResultsSummary from '../components/metrics/ResultsSummary';
import KeyboardHeatmap from '../components/ui/KeyboardHeatmap';

// Difficulty levels shared between English and snippet modes
const ENGLISH_DIFFICULTIES: ExerciseDifficulty[] = ['beginner', 'intermediate', 'advanced'];

export default function PracticePage() {
  const [selectedId, setSelectedId]     = useState<SidebarItemId | null>(null);
  const [selectedItem, setSelectedItem] = useState<SidebarItem | null>(null);
  const [difficulty, setDifficulty]     = useState<Difficulty | null>(null);

  const isEnglish = selectedId === 'english';

  // ── English path ────────────────────────────────────────────────────────────
  const englishDifficulty = difficulty as ExerciseDifficulty | null;
  const {
    exercise,
    refresh: refreshEnglish,
  } = useEnglishExercise(isEnglish ? englishDifficulty : null);

  const { callbackRef, heatmap, reset: resetHeatmap } = useKeystrokeHeatmap();

  // ── Snippet path ─────────────────────────────────────────────────────────────
  const backendLanguage = (!isEnglish && selectedItem?.backendLanguage) ? selectedItem.backendLanguage : null;
  const { snippet, loading, error, refresh: refreshSnippet } = useSnippet(
    backendLanguage as Language | null,
    difficulty,
  );

  // Active source — English exercise or backend snippet
  const source = isEnglish ? (exercise?.content ?? null) : (snippet?.source ?? null);

  const { handleKeyDown, restart, getCharStatus, phase, renderTick } = useTypingSession(
    source,
    isEnglish ? callbackRef : undefined,
  );

  const { wpm, cpm, accuracy, errorCount, elapsedMs, currentIndex } = useTypingStore();

  // Session result only logged for snippet-based sessions
  useSessionResult(
    phase,
    !isEnglish ? (snippet?.id ?? null) : null,
    wpm, cpm, accuracy, errorCount, elapsedMs,
  );

  const chars = useMemo(
    () => (source ? preprocessText(source) : []),
    [source],
  );

  // Next key the user must press — drives finger highlight on heatmap.
  // Derived from already-subscribed Zustand currentIndex + chars memo: no extra state.
  const nextKey = phase === 'active' || phase === 'idle'
    ? (chars[currentIndex] ?? null)
    : null;

  const handleSelect = useCallback((item: SidebarItem) => {
    setSelectedId(item.id);
    setSelectedItem(item);
    setDifficulty(null);
    restart();
    resetHeatmap();
    if (item.backendLanguage !== null) refreshSnippet();
  }, [restart, resetHeatmap, refreshSnippet]);

  const handleDifficultyChange = useCallback((d: Difficulty | null) => {
    setDifficulty(d);
    restart();
    if (isEnglish) {
      resetHeatmap();
      refreshEnglish();
    } else {
      refreshSnippet();
    }
  }, [isEnglish, restart, resetHeatmap, refreshEnglish, refreshSnippet]);

  const handleNext = useCallback(() => {
    restart();
    resetHeatmap();
    if (isEnglish) refreshEnglish();
    else refreshSnippet();
  }, [isEnglish, restart, resetHeatmap, refreshEnglish, refreshSnippet]);

  const handleRestart = useCallback(() => {
    restart();
    resetHeatmap();
  }, [restart, resetHeatmap]);

  const isComingSoon =
    selectedItem !== null &&
    selectedItem.backendLanguage === null &&
    selectedItem.id !== 'english';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="practice-layout">
      <LanguageSidebar selected={selectedId} onSelect={handleSelect} />

      <div className="practice-content">
        {!selectedItem ? (
          <WelcomeView onSelect={(lang) => {
            handleSelect({ id: lang as SidebarItemId, label: lang, backendLanguage: lang });
          }} />
        ) : isComingSoon ? (
          <div className="coming-soon">
            <p className="coming-soon-title">{selectedItem.label}</p>
            <p className="coming-soon-sub">snippets coming soon</p>
          </div>
        ) : isEnglish ? (
          // ── English mode ───────────────────────────────────────────────────
          <div className="practice-page">
            <div className="practice-controls">
              <div className="lang-selector" role="group" aria-label="Select difficulty">
                <button
                  className={`lang-btn${difficulty === null ? ' lang-btn--active' : ''}`}
                  onClick={() => handleDifficultyChange(null)}
                >
                  all
                </button>
                {ENGLISH_DIFFICULTIES.map((d) => (
                  <button
                    key={d}
                    className={`lang-btn${difficulty === d ? ' lang-btn--active' : ''}`}
                    onClick={() => handleDifficultyChange(d as Difficulty)}
                  >
                    {d}
                  </button>
                ))}
              </div>
              {exercise && (
                <span className="snippet-meta">
                  {exercise.title} · {exercise.difficulty} · {exercise.charCount} chars
                </span>
              )}
            </div>

            <LiveMetrics />

            {exercise ? (
              <>
                <ProgressBar current={currentIndex} total={chars.length} />
                <TypingArea onKeyDown={handleKeyDown} active={phase !== 'complete'}>
                  <SnippetDisplay
                    chars={chars}
                    getCharStatus={getCharStatus}
                    renderTick={renderTick}
                  />
                </TypingArea>

                {phase === 'idle' && (
                  <p className="start-hint">click here and start typing</p>
                )}

                <KeyboardHeatmap heatmap={heatmap} nextKey={nextKey} />
              </>
            ) : null}

            {phase === 'complete' && (
              <ResultsSummary onRestart={handleRestart} onNext={handleNext} />
            )}
          </div>
        ) : (
          // ── Snippet mode ───────────────────────────────────────────────────
          <div className="practice-page">
            <div className="practice-controls">
              <DifficultySelector value={difficulty} onChange={handleDifficultyChange} />
              {snippet && (
                <span className="snippet-meta">
                  {snippet.language === 'cpp' ? 'C++' : snippet.language.toUpperCase()}
                  {' · '}
                  {snippet.difficulty}
                  {' · '}
                  {snippet.charCount} chars
                </span>
              )}
            </div>

            <LiveMetrics />

            <SnippetLoader loading={loading} error={error}>
              {snippet && (
                <>
                  <ProgressBar current={currentIndex} total={chars.length} />
                  <TypingArea onKeyDown={handleKeyDown} active={phase !== 'complete'}>
                    <SnippetDisplay
                      chars={chars}
                      getCharStatus={getCharStatus}
                      renderTick={renderTick}
                    />
                  </TypingArea>

                  {phase === 'idle' && (
                    <p className="start-hint">click here and start typing</p>
                  )}
                </>
              )}
            </SnippetLoader>

            {phase === 'complete' && (
              <ResultsSummary onRestart={handleRestart} onNext={handleNext} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
